/**
 * Calculate transfer status for a Purchase Order based on transaction history.
 *
 * Status values:
 * - "Manually Marked as Completed" - Override when po.meta.manuallyMarkedCompleted is true
 * - "Not Transferred" - No transactions exist
 * - "Partially Transferred" - Some items transferred but not all match quantity
 * - "Fully Transferred" - All items have delta equal to quantity
 * - "Over Transferred" - Any item has delta exceeding quantity
 */
export function calculateTransferStatus(po) {
    if (po.meta && po.meta.manuallyMarkedCompleted) {
        return 'Manually Marked as Completed';
    }

    const transactions = (po.meta && po.meta.piShopTransfer && po.meta.piShopTransfer.transactions) || [];
    const items = po.items || [];

    if (transactions.length === 0) {
        return 'Not Transferred';
    }

    // Calculate absolute delta sum per product (transfers are negative, reverts are positive)
    const deltaByProduct = {};
    transactions.forEach(tx => {
        deltaByProduct[tx.productId] = (deltaByProduct[tx.productId] || 0) + tx.delta;
    });

    let allMatch = true;
    let anyTransferred = false;
    let anyOver = false;

    items.forEach(item => {
        const totalDelta = Math.abs(deltaByProduct[item.product.id] || 0);
        const expectedQty = item.quantity;

        if (totalDelta > 0) anyTransferred = true;
        if (totalDelta !== expectedQty) allMatch = false;
        if (totalDelta > expectedQty) anyOver = true;
    });

    if (anyOver) return 'Over Transferred';
    if (allMatch && anyTransferred) return 'Fully Transferred';
    if (anyTransferred) return 'Partially Transferred';
    return 'Not Transferred';
}

/**
 * Calculate the historic delta for a specific product from transactions.
 * Returns the sum of all deltas (negative for transfers out, positive for transfers in).
 */
export function calculateItemHistoricDelta(productId, transactions) {
    if (!transactions || !Array.isArray(transactions)) {
        return 0;
    }
    return transactions
        .filter(tx => tx.productId === productId)
        .reduce((sum, tx) => sum + (tx.delta || 0), 0);
}

/**
 * Get transactions for a specific product.
 */
export function getItemTransactions(productId, transactions) {
    if (!transactions || !Array.isArray(transactions)) {
        return [];
    }
    return transactions.filter(tx => tx.productId === productId);
}

/**
 * Get all unique product IDs from transactions (for showing removed items with history).
 */
export function getProductIdsWithTransactions(transactions) {
    if (!transactions || !Array.isArray(transactions)) {
        return [];
    }
    return [...new Set(transactions.map(tx => tx.productId))];
}
